import {Text} from '@gongxigames/framework/core/fragments/display/Text';
import {AdvancedTextParser} from '@gongxigames/framework/core/fragments/display/advancedText/AdvancedTextParser';
import {TextPart, SpritePart} from '@gongxigames/framework/core/fragments/display/advancedText/AdevancedTextData';
import {Container, Sprite, TextStyle} from 'pixi.js';
import {ObjectBuilder} from '@gongxigames/framework/core/services/ObjectBuilder';
import {consoleLogger} from '@gongxigames/framework/core/fragments/ConsoleLogger';
import {AUTOMATION_META, AutomationMeta} from '@gongxigames/framework/core/fragments/Autotest';

const RootSpriteName = 'ImagesForAdvancedText';

/**
 * Text that may contain images and different styles for text,
 * for this you need to add a tag <img sprite='spriteName'/> with the name of the sprite in the text
 * or tag <text style='custom_style'>text</text> for custom style
 * For example - "one <img sprite='spriteName'/> two <text style='custom_style'>parts</text>"
 * Image must be in sprite ImagesForAdvancedText
 * @deprecated Use RichText instead
 */
export class AdvancedText extends Container {

    // @inject(DI.ResourceManager)
    // protected _resourceManager: ResourceManager;

    public static styles: Record<string, TextStyle> = {};

    protected _style: TextStyle|Partial<TextStyle> = {};
    protected _text: string;
    protected _objectBuilder: ObjectBuilder;
    protected _maxWidth?: number;
    protected _resolution: number;

    // public static get haveRootSprite(): boolean {
    //     const {game_sprite, shared_sprite} =Loader.shared.resources;
    //
    //     return game_sprite.data[RootSpriteName] || shared_sprite.data[RootSpriteName];
    // }

    public static addStyle(name: string, style: TextStyle): void {
        this.styles[name] = style;
    }

    constructor(objectBuilder: ObjectBuilder, text: string, defaultStyle?: TextStyle, resolution?: number) {
        super();

        //         if (!AdvancedText.haveRootSprite) {
        //             console.warn(`AdvancedText::constructor - There is no sprite (${RootSpriteName}) in the scenes!
        // The game or shared must have a sprite (${RootSpriteName}) with images for the AdvancedText!`);
        //         }

        this._objectBuilder = objectBuilder;
        this._text = text;
        this._resolution = resolution ?? 1;

        if (defaultStyle) {
            this._style = defaultStyle;
        }

        this.updateText();
    }

    protected updateText(): void {
        this.removeChildren();

        const lines = AdvancedTextParser(this._text) as (TextPart | SpritePart)[][];
        const lineHeight = (new Text('height', this._style)).height;
        const getText = (lineText: string, styleName: string | undefined, x: number, y = 0): Text => {
            if (styleName && !AdvancedText.styles[styleName]) {
                consoleLogger.warn(`AdvancedText::updateText - There is no such style (${styleName}) for text (${lineText}), default style will be used!`);
            }

            const style = AdvancedText.styles[styleName as string] ?? this._style;
            const useStyle = Object.assign({}, style, {align: ''});
            const text = new Text(lineText, useStyle);

            text.resolution = this._resolution;
            text.position.set(x, y);

            return text;
        };

        const getSprite = (name: string, x: number, y: number): Sprite => {
            const sprite = this._objectBuilder.buildDisplayObjectByName(RootSpriteName, name) as Sprite;

            sprite.position.set(x, y);
            sprite.anchor.y = 0.5;

            return sprite;
        };

        let lineY = 0;

        for (const line of lines) {
            const lineSprite = new Sprite();
            let lineX = 0;

            lineSprite.y = lineY;

            line.forEach((partData: TextPart | SpritePart) => {
                let linePart: Sprite;

                if (partData.type === 'text') {
                    linePart = getText(partData.text, partData.style, lineX);
                } else if (partData.type === 'sprite') {
                    linePart = getSprite(partData.sprite, lineX, lineHeight / 2);

                    if (!linePart) {
                        linePart = getText(`<img sprite="${partData.sprite}">`, undefined, lineX);
                    }
                } else {
                    // TODO: refactor required
                    throw new Error('not implemented!');
                }

                if (linePart) {
                    lineSprite.addChild(linePart);
                }

                lineX = linePart.x + linePart.width;
            });

            this.addChild(lineSprite);

            lineY += lineHeight;
        }

        this.updateSize();
    }

    public set text(value: string) {
        this._text = value;
        this.updateText();
        this.updateSize();
    }

    public get text(): string {
        return this._text;
    }

    public updateSize(): void {
        this.children.forEach( child => {
            const lineWidth = child.getLocalBounds().width;

            switch (this._style!.align) {
                case 'center': {
                    child.x = -lineWidth / 2;
                }
                    break;
                case 'right': {
                    child.x = -lineWidth;
                }
                    break;
                default: {
                    child.x = 0;
                }
            }
        });

        if (this._maxWidth !== undefined && this.width > this._maxWidth) {
            this.height = this._maxWidth / this.width * this.height;
            this.width = this._maxWidth;
        }
    }

    public set maxWidth(value: number| undefined) {
        this._maxWidth = value;
        this.updateSize();
    }

    public get maxWidth(): number | undefined {
        return this._maxWidth;
    }

    [AUTOMATION_META](): AutomationMeta {
        return {
            type: 'text',
            text: this._text
        };
    }
}
